import { useMemo } from 'react';
import FilterTypes from '@constants/FilterTypes.ts';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import {
  CUSTOM_RANGE,
  LAST_MONTH,
  LAST_WEEK,
  NEXT_MONTH,
  NEXT_WEEK,
  THIS_MONTH,
  THIS_WEEK
} from '@constants/Kendo/rangeOptions';
import { actionTypes as dateRangePickerActionTypes } from '@components/Kendo/DateRangePickerReducer';
import { translate } from '@utils/i18n';
import { setFilterValue, setVisitDateFilter, toggleFilter } from '../Redux/visitsSlice';

const useFilters = ({
  filterValuesState,
  filterActiveState,
  tenant,
  assetsForSelect,
  inspectionTypesForSelect,
  dispatch,
  namespace,
  visitDateFilterMessage
}) => {
  const globalTerritoriesApplied = hasGlobalTerritoriesApplied();

  const handleFilterChangeDate = (fieldName) => {
    return (dateFieldName, value) => {
      dispatch(
        setVisitDateFilter({
          type: dateRangePickerActionTypes.DATE_RANGE_FILTER_CHANGED,
          field: fieldName,
          dateField: dateFieldName,
          value
        })
      );
    };
  };

  const handleFilterToggle = (fieldName) => {
    return () => {
      dispatch(toggleFilter({ field: fieldName }));
    };
  };

  const changeReactFilter = (fieldName) => {
    return (value) => {
      dispatch(setFilterValue({ field: fieldName, value }));
    };
  };

  const handleMultiSelectFilterChange = (fieldName) => {
    return (value) => {
      dispatch(setFilterValue({ field: fieldName, value }));
    };
  };

  const handleTypeFilterChange = (e) => {
    dispatch(setFilterValue({ field: 'type', value: e.target.value }));
  };

  const handleTypeFilterToggle = () => {
    dispatch(toggleFilter({ field: 'type' }));
  };

  const filtersList = useMemo(() => {
    const list = [
      {
        field: 'visitDate',
        locale: 'visit_date',
        type: 'dateRangePicker',
        active: true,
        locked: true,
        label: 'Visit Date',
        values: filterValuesState.visitDate,
        onChange: handleFilterChangeDate('visitDate'),
        onToggle: handleFilterToggle('visitDate'),
        dateRanges: [LAST_MONTH, LAST_WEEK, THIS_WEEK, NEXT_WEEK, THIS_MONTH, NEXT_MONTH, CUSTOM_RANGE],
        message: visitDateFilterMessage,
        resetDatesOnCustomRange: false
      },
      {
        field: 'statuses',
        locale: 'status',
        type: 'statuses',
        active: true,
        locked: true,
        inspectionStatusKeys: filterValuesState.inspectionStatusKeys ?? [],
        visitStatusKeys: filterValuesState.visitStatusKeys ?? [],
        defaultInspectionStatusKeys: ['pending'],
        defaultVisitStatusKeys: ['pending'],
        onInspectionStatusChange: changeReactFilter('inspectionStatusKeys'),
        onVisitStatusChange: changeReactFilter('visitStatusKeys'),
        onToggle: handleFilterToggle('statuses')
      },
      {
        field: 'technicianTeam',
        locale: 'team',
        type: FilterTypes.GenericMultiSelect,
        active: filterActiveState.technicianTeam,
        locked: false,
        value: filterValuesState.technicianTeam,
        onChange: handleMultiSelectFilterChange('technicianTeam'),
        onToggle: handleFilterToggle('technicianTeam')
      },
      {
        field: 'assignee',
        locale: tenant.flags.subcontractorsEnabled ? 'assigned_to' : 'technician',
        type: 'assignee',
        active: filterActiveState.assignee,
        locked: false,
        teamIds: filterValuesState.technicianTeam === '' ? null : filterValuesState.technicianTeam,
        technicianIds: filterValuesState.assigneeTechIds === 'all' ? [] : filterValuesState.assigneeTechIds ?? [],
        subcontractorIds: filterValuesState.assigneeSubIds === 'all' ? [] : filterValuesState.assigneeSubIds ?? [],
        onTechChange: changeReactFilter('assigneeTechIds'),
        onSubChange: changeReactFilter('assigneeSubIds'),
        onToggle: handleFilterToggle('assignee')
      },
      {
        field: 'asset',
        locale: 'asset',
        type: FilterTypes.TreeMultiSelect,
        active: filterActiveState.asset,
        value: filterValuesState.asset,
        optionsForSelect: assetsForSelect,
        onChange: handleMultiSelectFilterChange('asset'),
        onToggle: handleFilterToggle('asset')
      },
      {
        field: 'account',
        locale: 'account',
        type: FilterTypes.GenericMultiSelect,
        active: filterActiveState.account,
        value: filterValuesState.account,
        onChange: handleMultiSelectFilterChange('account'),
        onToggle: handleFilterToggle('account')
      },
      {
        field: 'building',
        locale: 'building',
        type: FilterTypes.GenericMultiSelect,
        active: filterActiveState.building,
        locked: false,
        value: filterValuesState.building,
        onChange: handleMultiSelectFilterChange('building'),
        onToggle: handleFilterToggle('building')
      },
      {
        field: 'tags',
        backendField: 'tags',
        locale: 'inspection_tags',
        type: FilterTypes.GenericMultiSelect,
        active: filterActiveState.tags,
        locked: false,
        value: filterValuesState.tags,
        onChange: handleMultiSelectFilterChange('tags'),
        onToggle: handleFilterToggle('tags')
      },
      {
        field: 'buildingTags',
        backendField: 'tags',
        locale: 'building_tags',
        type: FilterTypes.GenericMultiSelect,
        active: filterActiveState.buildingTags,
        value: filterValuesState.buildingTags,
        onChange: handleMultiSelectFilterChange('buildingTags'),
        onToggle: handleFilterToggle('buildingTags')
      },
      {
        field: 'accountTags',
        backendField: 'tags',
        locale: 'account_tags',
        type: FilterTypes.GenericMultiSelect,
        active: filterActiveState.accountTags,
        value: filterValuesState.accountTags,
        onChange: handleMultiSelectFilterChange('accountTags'),
        onToggle: handleFilterToggle('accountTags')
      },
      {
        field: 'workOrderTags',
        backendField: 'tags',
        locale: 'work_order_tags',
        type: FilterTypes.GenericMultiSelect,
        active: filterActiveState.workOrderTags,
        value: filterValuesState.workOrderTags,
        onChange: handleMultiSelectFilterChange('workOrderTags'),
        onToggle: handleFilterToggle('workOrderTags')
      },
      {
        field: 'zipCode',
        backendField: 'zip_code',
        locale: 'postal_code',
        type: 'textBox',
        active: filterActiveState.zipCode,
        locked: false,
        value: filterValuesState.zipCode,
        onChange: changeReactFilter('zipCode'),
        onToggle: handleFilterToggle('zipCode')
      },
      {
        field: 'type',
        locale: 'visit_type',
        type: 'oldCommonSelect',
        active: filterActiveState.type,
        locked: false,
        optionsForSelect: [
          { value: 'WorkOrder', label: 'Work Order' },
          { value: 'Inspection', label: 'Inspection' }
        ],
        value: filterValuesState.type,
        onChange: handleTypeFilterChange,
        onToggle: handleTypeFilterToggle
      },
      {
        field: 'inspectionType',
        locale: 'inspection_type',
        type: FilterTypes.GenericMultiSelect,
        active: filterValuesState.type === 'Inspection',
        locked: true,
        optionsForSelect: inspectionTypesForSelect.map((opt) => ({ title: opt.label, value: opt.value })),
        value: filterValuesState.inspectionType,
        onChange: handleMultiSelectFilterChange('inspectionType'),
        onToggle: handleFilterToggle('inspectionType'),
        tooltip:
          filterValuesState.type === 'Inspection' ? undefined : translate('inspection_type_tooltip', { namespace })
      }
    ];

    if (!globalTerritoriesApplied) {
      list.push({
        field: 'territory',
        locale: 'territory',
        type: FilterTypes.GenericMultiSelect,
        active: filterActiveState.territory,
        locked: false,
        value: filterValuesState.territory,
        withUnassigned: true,
        onChange: handleMultiSelectFilterChange('territory'),
        onToggle: handleFilterToggle('territory')
      });
    }

    return list;
  }, [
    filterValuesState,
    filterActiveState,
    tenant,
    assetsForSelect,
    inspectionTypesForSelect,
    dispatch,
    namespace,
    visitDateFilterMessage,
    globalTerritoriesApplied
  ]);

  return filtersList;
};

export default useFilters;
